<template>
  <el-card>
    <div class="page-head">
      <div>
        <div class="section-title">测评记录管理</div>
        <p class="hint">
          可查询表观测评、功能测评各模块记录；在「设置模块权限」中控制小程序全局展示。
        </p>
      </div>
      <el-button type="primary" plain @click="openPermissionDialog">设置模块权限</el-button>
    </div>

    <div class="toolbar">
      <el-select v-model="moduleKey" clearable placeholder="测评模块" style="width: 200px" @change="onFilterChange">
        <el-option v-for="m in modules" :key="m.moduleKey + '-' + (m.category || '')" :label="m.title" :value="m.moduleKey" />
      </el-select>
      <el-select v-model="hiddenFilter" clearable placeholder="模块展示状态" style="width: 160px" @change="onFilterChange">
        <el-option label="展示中模块" :value="false" />
        <el-option label="已隐藏模块" :value="true" />
      </el-select>
      <el-input
        v-model="keyword"
        placeholder="openid / 昵称 / 手机"
        clearable
        style="width: 260px"
        @keyup.enter.native="load"
      />
      <el-button type="primary" @click="load">查询</el-button>
    </div>
    <el-table :data="rows" border stripe v-loading="loading" style="width: 100%; margin-top: 12px">
      <el-table-column label="类型" width="100">
        <template slot-scope="s">
          <el-tag size="mini" :type="s.row.category === 'scale' ? 'warning' : 'primary'">
            {{ s.row.category === 'scale' ? '表观' : '功能' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="moduleTitle" label="测评模块" min-width="160" />
      <el-table-column prop="recordId" label="记录ID" width="90" />
      <el-table-column prop="userName" label="昵称" width="120" show-overflow-tooltip />
      <el-table-column prop="openid" label="openid" min-width="140" show-overflow-tooltip />
      <el-table-column prop="userPhone" label="手机" width="120" />
      <el-table-column label="测评时间" width="170">
        <template slot-scope="s">{{ formatTime(s.row.createTime) }}</template>
      </el-table-column>
      <el-table-column label="模块展示" width="110">
        <template slot-scope="s">
          <el-tag :type="s.row.moduleHidden ? 'info' : 'success'" size="mini">{{ s.row.moduleHidden ? '已隐藏' : '展示中' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="90" fixed="right">
        <template slot-scope="s">
          <el-button type="text" @click="openDetail(s.row)">查看</el-button>
        </template>
      </el-table-column>
    </el-table>
    <el-pagination
      style="margin-top: 12px"
      background
      layout="total, prev, pager, next"
      :total="total"
      :page-size="size"
      :current-page.sync="page"
      @current-change="load"
    />

    <el-dialog title="设置模块权限" :visible.sync="permissionVisible" width="720px" @open="loadModuleVisibility">
      <p class="dialog-hint">
        隐藏某模块后，<strong>所有用户</strong>在小程序首页入口、「我的」报告菜单、测评记录与对比分析中均不再展示该模块。
      </p>
      <div class="dialog-section-title">表观测评模块</div>
      <el-table :data="scaleRows" border stripe v-loading="moduleLoading" size="small">
        <el-table-column prop="title" label="测评模块" min-width="200" />
        <el-table-column label="小程序展示" width="120">
          <template slot-scope="s">
            <el-tag :type="s.row.hidden ? 'info' : 'success'" size="mini">{{ s.row.hidden ? '已隐藏' : '展示中' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="140">
          <template slot-scope="s">
            <el-switch
              :value="!s.row.hidden"
              active-text="展示"
              inactive-text="隐藏"
              @change="(val) => onModuleSwitch(s.row, val)"
            />
          </template>
        </el-table-column>
      </el-table>

      <div class="dialog-section-title dialog-section-gap">功能测评模块</div>
      <el-table :data="functionalRows" border stripe v-loading="moduleLoading" size="small">
        <el-table-column prop="title" label="测评模块" min-width="200" />
        <el-table-column label="小程序展示" width="120">
          <template slot-scope="s">
            <el-tag :type="s.row.hidden ? 'info' : 'success'" size="mini">{{ s.row.hidden ? '已隐藏' : '展示中' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="140">
          <template slot-scope="s">
            <el-switch
              :value="!s.row.hidden"
              active-text="展示"
              inactive-text="隐藏"
              @change="(val) => onModuleSwitch(s.row, val)"
            />
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer">
        <el-button @click="permissionVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <el-dialog
      :title="detailTitle"
      :visible.sync="detailVisible"
      width="480px"
      custom-class="assessment-result-dialog"
      :close-on-click-modal="false"
      @closed="resetDetail"
    >
      <div v-loading="detailLoading" class="detail-dialog-body">
        <assessment-result-panel v-if="detail" :detail="detail" :metrics="detailMetrics" />
        <div v-else-if="!detailLoading" class="detail-empty">暂无数据</div>
      </div>
      <span slot="footer">
        <el-button @click="detailVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </el-card>
</template>

<script>
import http from '../api/http';
import AssessmentResultPanel from '../components/AssessmentResultPanel.vue';

export default {
  name: 'FunctionalResultsView',
  components: {
    AssessmentResultPanel
  },
  data() {
    return {
      modules: [],
      scaleRows: [],
      functionalRows: [],
      moduleLoading: false,
      permissionVisible: false,
      rows: [],
      keyword: '',
      moduleKey: '',
      hiddenFilter: null,
      page: 1,
      size: 20,
      total: 0,
      loading: false,
      detailVisible: false,
      detailLoading: false,
      detail: null,
      detailMetrics: [],
      detailTitle: '测评结果详情'
    };
  },
  mounted() {
    this.loadModuleCatalog();
    this.load();
  },
  methods: {
    openPermissionDialog() {
      this.permissionVisible = true;
    },
    loadModuleCatalog() {
      http
        .get('/admin/functional-results/module-catalog')
        .then(({ data }) => {
          const raw = data.data != null ? data.data : data;
          this.modules = Array.isArray(raw) ? raw : [];
        })
        .catch(() => {});
    },
    loadModuleVisibility() {
      this.moduleLoading = true;
      http
        .get('/admin/functional-results/module-visibility')
        .then(({ data }) => {
          const raw = data.data != null ? data.data : data;
          if (raw && Array.isArray(raw.scale)) {
            this.scaleRows = raw.scale;
            this.functionalRows = Array.isArray(raw.functional) ? raw.functional : [];
          } else if (Array.isArray(raw)) {
            this.scaleRows = [];
            this.functionalRows = raw;
          } else {
            this.scaleRows = [];
            this.functionalRows = [];
          }
        })
        .catch((e) => {
          this.$message.error((e.response && e.response.data && e.response.data.message) || '加载模块配置失败');
        })
        .finally(() => {
          this.moduleLoading = false;
        });
    },
    onModuleSwitch(row, visible) {
      const hidden = !visible;
      const action = hidden ? '隐藏' : '恢复展示';
      this.$confirm(`确定${action}模块「${row.title}」？隐藏后所有用户小程序端均不可见该模块。`, '提示', {
        type: 'warning'
      })
        .then(() =>
          http.patch('/admin/functional-results/module-visibility', {
            category: row.category,
            moduleKey: row.moduleKey,
            hidden
          })
        )
        .then(() => {
          this.$message.success(`${action}成功`);
          this.loadModuleVisibility();
          this.load();
        })
        .catch((e) => {
          if (e === 'cancel') return;
          this.$message.error((e.response && e.response.data && e.response.data.message) || `${action}失败`);
        });
    },
    onFilterChange() {
      this.page = 1;
      this.load();
    },
    load() {
      this.loading = true;
      const params = { page: this.page, size: this.size };
      if (this.moduleKey) params.moduleKey = this.moduleKey;
      if (this.keyword) params.keyword = this.keyword.trim();
      if (this.hiddenFilter === true || this.hiddenFilter === false) {
        params.hidden = this.hiddenFilter;
      }
      http
        .get('/admin/functional-results', { params })
        .then(({ data }) => {
          const raw = data.data != null ? data.data : data;
          this.rows = (raw && raw.records) || [];
          this.total = raw && raw.total != null ? Number(raw.total) : 0;
        })
        .catch((e) => {
          this.$message.error((e.response && e.response.data && e.response.data.message) || '加载失败');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    formatTime(t) {
      if (!t) return '—';
      const d = new Date(t);
      if (Number.isNaN(d.getTime())) return String(t);
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    openDetail(row) {
      if (!row || !row.tableName || row.recordId == null) {
        this.$message.warning('记录信息不完整');
        return;
      }
      this.detailTitle = `测评结果 · ${row.moduleTitle || ''}`;
      this.detailVisible = true;
      this.detailLoading = true;
      this.detail = null;
      this.detailMetrics = [];
      http
        .get('/admin/functional-results/detail', {
          params: {
            tableName: row.tableName,
            recordId: row.recordId
          }
        })
        .then(({ data }) => {
          const raw = data.data != null ? data.data : data;
          this.detail = raw || null;
          this.detailMetrics = (raw && Array.isArray(raw.metrics) ? raw.metrics : []).map((m) => ({
            section: m.section || '测评结果',
            label: m.label,
            value: m.value
          }));
        })
        .catch((e) => {
          this.$message.error((e.response && e.response.data && e.response.data.message) || '加载详情失败');
          this.detailVisible = false;
        })
        .finally(() => {
          this.detailLoading = false;
        });
    },
    resetDetail() {
      this.detail = null;
      this.detailMetrics = [];
    }
  }
};
</script>

<style scoped>
.page-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}
.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}
.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin-top: 16px;
}
.hint {
  margin: 8px 0 0;
  font-size: 13px;
  color: #909399;
  line-height: 1.5;
}
.dialog-hint {
  margin: 0 0 12px;
  font-size: 13px;
  color: #909399;
  line-height: 1.5;
}
.dialog-section-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
}
.dialog-section-gap {
  margin-top: 20px;
}
.detail-dialog-body {
  min-height: 120px;
}
.detail-empty {
  color: #909399;
  font-size: 13px;
  padding: 24px 0;
  text-align: center;
}
</style>

<style>
.assessment-result-dialog .el-dialog__body {
  padding: 0;
  background: #e2ebf4;
}
</style>
